import { TablePagination, Theme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { EmptyText } from "components/Table/styles";
import { Column, getComparator, stableSort } from "components/Table/Table";
import useText from "constants/text";
import { Hub } from "generated/graphql";
import React, { useState } from "react";
import { SimpleHeader } from "./SimpleHeader";
import { SimpleRow } from "./SimpleRow";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      overflow: "scroll",
      [theme.breakpoints.down("xs")]: {
        width: "108%",
      },
    },
    th: {
      textAlign: "left",
      padding: 8,
    },
    td: {
      textAlign: "left",
      padding: 8,
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    simpleRow: {
      position: "relative",
      top: "46px",
      border: "1px solid rgba(57, 158, 191, 0.23)",
      borderRadius: "5px",
      margin: "15px 0px",
    },
    tableBody: {
      marginBottom: "15px",
      [theme.breakpoints.down("xs")]: { overflowX: "none" },
    },
  })
);

type Order = "asc" | "desc";
export interface SimpleTableProps<RowData, RowDataKey extends keyof RowData> {
  onRowClick?(data: RowData): void;
  handleGetRowsChecked?(data: string[]): void;
  onRowDelete?(data: RowData): void;
  onRowEdit?(data: RowData): void;
  onRowChecked?(data: RowData): void;
  onAllRowChecked?(data: Array<string>): void;
  rowsIdentifier?: RowDataKey;
  data?: RowData[];
  columns?: Array<Column<RowData>>;
  isLoading?: boolean;
  isShowRelay?: boolean;
  refetchTableData?(): Promise<any>;
  hasSelection?: boolean;
  orderByIdentifier?: RowDataKey;
  rowsPerPageOptions?: Array<number>;
  asAction?: boolean;
  hasChecked?: boolean;
  isRelayPoint?: boolean;
  relayPointCity?: any[];
  handleHubSelect?(data: Hub): void;
  handleGetReceiverHub?(data: Hub, rowId: string): void;
}
export function SimpleTable<RowData, RowDataKey extends keyof RowData>({
  columns = [],
  hasSelection = false,
  hasChecked = false,
  isShowRelay,
  onRowClick,
  rowsIdentifier,
  onRowDelete,
  onRowEdit,
  data = [],
  onAllRowChecked,
  onRowChecked,
  orderByIdentifier = rowsIdentifier,
  asAction = false,
  rowsPerPageOptions,
  isRelayPoint,
  relayPointCity,
  handleHubSelect,
  handleGetRowsChecked,
  handleGetReceiverHub,
}: SimpleTableProps<RowData, RowDataKey>) {
  const styles = useStyles();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof RowData>(orderByIdentifier);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [check, setCheck] = useState(hasChecked);
  const { text } = useText();
  const handlerSelectAll = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelecteds = data.map((currentCell: any) => currentCell[rowsIdentifier]);
        setSelected(newSelecteds);
        setCheck(!hasChecked);
        onAllRowChecked && onAllRowChecked(newSelecteds);
      } else {
        setSelected([]);
        setCheck(hasChecked);
        onAllRowChecked && onAllRowChecked([]);
      }
    },
    [data, rowsIdentifier, onAllRowChecked]
  );
  React.useEffect(() => {
    handleGetRowsChecked(selected);
  }, [selected]);
  const handleRowSelectClick = React.useCallback(
    (event: React.MouseEvent<unknown>, id: string) => {
      const selectedIndex = selected.indexOf(id);

      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const totalRowsSelectedCount = React.useMemo(() => selected.length, [selected]);
  const totalDataToShowCount = React.useMemo(() => data?.length || -1, [data]);
  const isRowSelected = React.useCallback((name: string) => selected.includes(name), [selected]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RowData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedRows = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    const toSortedData = data as any;
    const result = stableSort(toSortedData, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length, order, orderBy, page, rowsPerPage, data, JSON.parse(JSON.stringify(data))]);
  return (
    <div className={styles.root}>
      <table className={styles.root}>
        <SimpleHeader
          columns={columns}
          onRequestSort={handleRequestSort}
          hasSelection={hasSelection}
          hasChecked={hasChecked}
          asAction={asAction}
          onAllRowChecked={handlerSelectAll}
          rowCount={totalDataToShowCount}
          selectedRowsCount={totalRowsSelectedCount}
          orderBy={orderBy}
          order={order}
        />
        <tbody className={styles.tableBody}>
          {sortedRows?.map((rowContent, index) => (
            <div className={styles.simpleRow}>
              <SimpleRow
                key={`${rowContent[rowsIdentifier]}${index}`}
                columns={columns}
                rowContent={rowContent as any}
                rowsIdentifier={rowsIdentifier}
                hasSelection={hasSelection}
                hasChecked={hasChecked}
                onRowEdit={onRowEdit}
                onRowDelete={onRowDelete}
                onRowChecked={onRowChecked}
                selectRowsCount={totalRowsSelectedCount}
                rowsCount={totalDataToShowCount}
                isRowSelected={isRowSelected}
                handleRowSelectClick={handleRowSelectClick}
                onRowClick={onRowClick}
                isRelayPoint={isRelayPoint}
                isShowRelay={isShowRelay}
                relayPointCity={relayPointCity}
                handleHubSelect={handleHubSelect}
                handleGetReceiverHub={handleGetReceiverHub}
              />
            </div>
          ))}
        </tbody>
      </table>

      {Array.isArray(data) && data.length === 0 ? (
        <EmptyText> {text.all.empty_table} </EmptyText>
      ) : (
        <TablePagination
          style={{ marginTop: "60px" }}
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={data?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </div>
  );
}
